import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { track } from "@vercel/analytics";
import SEOHead from "../../components/SEO/SEOHead";
import { generateWebPageStructuredData } from "../../components/SEO/StructuredData";
import AuthModal from "../../components/AuthModal/AuthModal";
import PayoutTableWorkspace from "../../components/organizations/PayoutTables/PayoutTableWorkspace";
import { usePayoutWorkspace } from "../../components/organizations/PayoutTables/usePayoutWorkspace";
import { useAuth } from "../../context/AuthContext";
import { notify } from "../../components/UI/DesignStandards/Notifications/notify";
import { downloadPayoutExportPng } from "../../utility/exportPayoutTable";
import {
  PAYOUT_SHAPES,
  buildPayoutExportTable,
  buildPayoutTableCsv,
  columnForEntries,
  downloadPayoutTableCsv,
  estimatedPrizeFund,
  isPayoutPlanningComplete,
} from "../../utility/generateShapedPayoutTable";

const PayoutCalculator = () => {
  const { user } = useAuth();
  const workspace = usePayoutWorkspace({ canEdit: true });
  const [entries, setEntries] = useState("");
  const [exportingKey, setExportingKey] = useState("");
  const [showAuthModal, setShowAuthModal] = useState(false);
  const {
    showMore,
    setShowMore,
    advanced,
    planning,
    payoutData,
    isEditing,
    isRecommended,
    optionsLocked,
    calculateColumnTotal,
    isColumnValid,
    handlePercentageChange,
    handlePlanningField,
    handleAdvancedField,
    handleResetRecommended,
    handleAddColumn,
    handleDeleteColumn,
    handleStartEditCells,
    handleFinishEditing,
    handleCancelEditing,
  } = workspace;

  const column = useMemo(
    () =>
      columnForEntries(
        entries,
        advanced.ratioNumerator,
        advanced.ratioDenominator,
        advanced.places
      ),
    [
      entries,
      advanced.ratioNumerator,
      advanced.ratioDenominator,
      advanced.places,
    ]
  );

  const prizeFund = estimatedPrizeFund(entries, planning.prizePerEntry);

  const entriesHint = useMemo(() => {
    if (!column.highlighted) {
      return "Field size only highlights a column. It does not change the percentages.";
    }
    const ratio = `${advanced.ratioNumerator} in ${advanced.ratioDenominator}`;
    const fundNote =
      prizeFund > 0 ? ` Prize fund is $${prizeFund}.` : "";
    if (column.capped) {
      return `${entries} entries at ${ratio} would pay ${column.paid} places. This table covers ${advanced.places}, so the ${column.highlighted}-place column is highlighted.${fundNote}`;
    }
    return `${entries} entries at ${ratio} is the ${column.highlighted}-place column.${fundNote}`;
  }, [column, entries, advanced, prizeFund]);

  const prizeFundForQuantity =
    prizeFund > 0 && column.highlighted
      ? (qty) => (Number(qty) === Number(column.highlighted) ? prizeFund : 0)
      : undefined;

  const exportCaption = useMemo(() => {
    const shapeName = PAYOUT_SHAPES[advanced.shape]?.name || "Payout";
    const bits = [
      shapeName,
      `${advanced.ratioNumerator} in ${advanced.ratioDenominator}`,
    ];
    if (entries) bits.push(`${entries} entries`);
    if (prizeFund > 0) bits.push(`$${prizeFund} prize fund`);
    return bits.join(" · ");
  }, [advanced, entries, prizeFund]);

  const handleExport = async (scope, format) => {
    if (scope === "column" && !column.highlighted) return;
    const quantity = scope === "column" ? column.highlighted : null;
    const visual = format !== "csv";
    const includeDollars =
      scope === "column"
        ? isPayoutPlanningComplete(planning)
        : format === "csv" && isPayoutPlanningComplete(planning);
    const title =
      scope === "column"
        ? `${column.highlighted}-place payout`
        : "Payout table";
    const filename =
      scope === "column"
        ? `bowling-payout-${column.highlighted}-places.${format}`
        : `bowling-payout-table.${format}`;
    const exportOptions = {
      payoutData,
      quantity,
      planning,
      ratioNumerator: advanced.ratioNumerator,
      ratioDenominator: advanced.ratioDenominator,
      prizeFund,
      prizeFundForQuantity,
      includeDollars,
      headerStyle: visual ? "sheet" : "csv",
      dollarStyle: visual ? "currency" : "plain",
    };

    const key = `${scope}-${format}`;
    setExportingKey(key);
    try {
      if (format === "csv") {
        downloadPayoutTableCsv(filename, buildPayoutTableCsv(exportOptions));
        return;
      }
      if (format !== "png") return;
      const table = buildPayoutExportTable(exportOptions);
      await downloadPayoutExportPng({
        filename,
        headers: table.headers,
        rows: table.rows,
        title,
        subtitle: exportCaption,
        kind: table.kind,
      });
    } catch (error) {
      notify.error(
        error?.message || "Could not export the payout table."
      );
    } finally {
      setExportingKey("");
    }
  };

  return (
    <>
      <SEOHead
        title="Bowling Payout Calculator | Bowling Tournament App"
        description="Percent table of places paid. Standard is 1 in 6. First place is capped at 40% on larger columns, the USBC Rule 308 limit for 100 or more entries. Nothing is saved."
        keywords={[
          "bowling payout calculator",
          "bowling tournament payout table",
          "USBC payout chart",
        ]}
        canonicalUrl="/bowling-payout-calculator"
        ogType="website"
        structuredData={generateWebPageStructuredData({
          name: "Bowling payout calculator",
          description:
            "Percent table of places paid. Standard is 1 in 6. First place is capped at 40% on larger columns, the USBC Rule 308 limit for 100 or more entries. Nothing is saved.",
          url: "/bowling-payout-calculator",
        })}
      />

      <div className="min-h-screen bg-brand-cream">
        <section
          aria-labelledby="payout-calculator-title"
          className="bg-brand-charcoal text-brand-cream"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-16">
            <p className="text-brand-mist font-display tracking-widest uppercase text-sm mb-3">
              Bowling Tournament App
            </p>
            <h1
              id="payout-calculator-title"
              className="font-display font-bold tracking-tight text-4xl md:text-5xl text-brand-cream leading-tight max-w-4xl"
            >
              Bowling payout calculator
            </h1>
            <p className="mt-4 text-base md:text-lg text-brand-mist max-w-2xl">
              Build a percent table for a tournament. Standard is 1 in 6.
            </p>
            {user ? (
              <div className="mt-6 md:mt-8">
                <Link
                  to="/organization/payout-tables"
                  onClick={() =>
                    track("payout_calculator_cta_click", {
                      location: "calculator_hero",
                      action: "org_tables",
                    })
                  }
                  className="btn-brand md:px-8 md:py-3"
                >
                  Organization payout tables
                </Link>
              </div>
            ) : (
              <div className="mt-6 md:mt-8 flex flex-col sm:flex-row gap-3">
                <Link
                  to="/signup"
                  state={{ from: "/create-tournament" }}
                  onClick={() =>
                    track("payout_calculator_cta_click", {
                      location: "calculator_hero",
                      action: "signup",
                    })
                  }
                  className="btn-brand md:px-8 md:py-3"
                >
                  Sign up
                </Link>
                <Link
                  to="/create-tournament"
                  onClick={(e) => {
                    track("payout_calculator_cta_click", {
                      location: "calculator_hero",
                      action: "run_tournament",
                    });
                    e.preventDefault();
                    setShowAuthModal(true);
                  }}
                  className="inline-flex items-center justify-center rounded-lg bg-brand-cream text-brand-charcoal font-display font-bold tracking-wide px-4 py-2 md:px-8 md:py-3 hover:bg-white transition-colors"
                >
                  Run a tournament
                </Link>
              </div>
            )}
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-10 sm:pt-8 sm:pb-12 space-y-6">
          <PayoutTableWorkspace
            canEdit
            showMore={showMore}
            onToggleMore={() => setShowMore((open) => !open)}
            places={advanced.places}
            shape={advanced.shape}
            ratioNumerator={advanced.ratioNumerator}
            ratioDenominator={advanced.ratioDenominator}
            payoutData={payoutData}
            planning={planning}
            onPlanningChange={handlePlanningField}
            isEditing={isEditing}
            onChangeField={handleAdvancedField}
            isRecommended={isRecommended}
            onResetRecommended={handleResetRecommended}
            optionsLocked={optionsLocked}
            onEditCells={handleStartEditCells}
            onSave={handleFinishEditing}
            onCancel={handleCancelEditing}
            canSave
            allowPersist={false}
            saveLabel="Done"
            idPrefix="public-payout"
            entries={entries}
            onEntriesChange={setEntries}
            entriesHint={entriesHint}
            onExport={handleExport}
            exportColumnDisabled={!column.highlighted}
            exportingKey={exportingKey}
            previewQuantity={column.highlighted}
            calculateColumnTotal={calculateColumnTotal}
            isColumnValid={isColumnValid}
            onPercentageChange={handlePercentageChange}
            onAddColumn={handleAddColumn}
            onDeleteColumn={handleDeleteColumn}
          />

          <section className="card-board p-4 sm:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <p role="note" className="text-sm text-brand-muted max-w-xl">
              {user
                ? "Nothing is saved on this page. Keep a payout table on your organization."
                : "Nothing is saved on this page. Sign up to keep a payout table on your organization and run the event."}
            </p>
            {user ? (
              <Link
                to="/organization/payout-tables"
                onClick={() =>
                  track("payout_calculator_cta_click", {
                    location: "calculator_after_table",
                    action: "org_tables",
                  })
                }
                className="btn-brand shrink-0 md:px-8 md:py-3"
              >
                Organization payout tables
              </Link>
            ) : (
              <Link
                to="/signup"
                state={{ from: "/create-tournament" }}
                onClick={() =>
                  track("payout_calculator_cta_click", {
                    location: "calculator_after_table",
                    action: "signup",
                  })
                }
                className="btn-brand shrink-0 md:px-8 md:py-3"
              >
                Sign up
              </Link>
            )}
          </section>

          <details className="card-board">
            <summary className="cursor-pointer px-4 py-3 sm:px-6 min-h-11 text-brand-charcoal">
              <h2 className="heading-display text-lg inline">How this works</h2>
            </summary>
            <div className="px-4 sm:px-6 pt-4 pb-5 space-y-4 text-base leading-relaxed text-brand-muted border-t border-brand-charcoal/10">
              <p>
                Each column is how many places are paid. Read down the column.
                Each number is that place's percent of the prize fund, and
                the column adds to 100%. The recommended table is Standard, 30
                places, at 1 in 6. Use the column for how many places the
                tournament pays.
              </p>
              <p>
                Entries is optional. It highlights the column for that field at
                your ratio: places paid is the field times the first number,
                divided by the second, rounded up. It does not change the
                percentages. If the field would pay more places than the table
                covers, the last column is highlighted.
              </p>
              <p>
                Prize per entry, under More options, turns those percents into
                dollars. With entries filled, the prize fund is entries times
                prize per entry, and dollars show on the highlighted column.
                Without entries, each column uses the field that column implies.
                Last place floor is an optional whole-dollar minimum for the last
                paid place. It applies only when prize per entry is filled.
                Payouts round to whole dollars. Leftover dollars go to the top
                places, skipping a place already at a cap.
              </p>
              <p>
                Export the highlighted column or the full table as CSV or PNG. A
                column export includes dollars when prize per entry is set. The
                full-table PNG is percents. The full-table CSV adds dollar
                columns when prize per entry is set. With entries filled, only
                the highlighted column has dollar amounts.
              </p>
              <p>
                USBC Rule 308 limits first place to 40% of the prize fund when an
                event has 100 or more entries. This table starts that cap at the
                place column for the ratio. At the standard 1 in 6, that is the
                16-place column. It does not read a live entry count, and it does
                not enforce the rest of Rule 308: a last-place prize at least
                equal to the prize fee, one prize for each 10 entries, or second
                place at least half of first.{" "}
                <Link
                  to="/bowling-tournament-payouts"
                  className="text-brand-charcoal hover:underline"
                >
                  Tournament payouts
                </Link>{" "}
                is where a saved table is used on the event.
              </p>
              <p>Nothing is saved on this page.</p>
            </div>
          </details>
        </div>
      </div>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
      />
    </>
  );
};

export default PayoutCalculator;
